import { explicitMaybe } from '@execonline-inc/decoders';
import { array, boolean, dateISO, Decoder, field, number, string, succeed } from 'jsonous';
import { resourceDecoder } from '../../../../Resource/Decoders';
import {
  Collection,
  Member,
  UserGroupWithAssociations,
} from '../../Participants/AddParticipant/Stores/ParticipantOptionsStore/Types';
import {
  DomainMismatchAlert,
  FailedParticipant,
  ImportOptions,
  ImportOptionsResource,
  ImportResult,
  ImportResultResource,
  License,
  SuccessfulParticipant,
  UpdatedParticipant,
} from './Types';

const collectionDecoder: Decoder<Collection> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('learningDesignCount', field('learning_design_count', number));

const memberDecoder: Decoder<Member> = succeed({})
  .assign('id', field('id', number))
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string))
  .assign('email', field('email', string));

const groupDecoder: Decoder<UserGroupWithAssociations> = succeed({})
  .assign('id', field('id', number))
  .assign('name', field('name', string))
  .assign('organization', field('organization', string))
  .assign('organizationId', field('organization_id', number))
  .assign('memberCount', field('member_count', number))
  .assign('assignedLicenseCount', field('assigned_license_count', number))
  .assign('assignedLicenseIds', field('assigned_license_ids', array(number)))
  .assign('associatedLicenseIds', field('associated_license_ids', array(number)))
  .assign('collections', field('collections', array(collectionDecoder)))
  .assign('members', field('members', array(memberDecoder)));

const licenseDecoder: Decoder<License> = succeed({})
  .assign('id', field('id', number))
  .assign('licenseName', field('license_name', explicitMaybe(string)))
  .assign('licenseTypeName', field('license_type_name', string))
  .assign('quantityAvailable', field('quantity_available', number))
  .assign('startOn', field('start_on', dateISO))
  .assign('endOn', field('end_on', dateISO));

const importOptionsDecoder: Decoder<ImportOptions> = succeed({})
  .assign('userGroups', field('user_groups', array(groupDecoder)))
  .assign('licenses', field('licenses', array(licenseDecoder)))
  .assign('canAddAsAuditor', field('can_add_as_auditor', boolean))
  .assign('allowedEmailDomains', field('allowed_email_domains', array(string)));

export const importOptionsResourceDecoder: Decoder<ImportOptionsResource> =
  resourceDecoder(importOptionsDecoder);

// Result decoders
const successfulParticipantDecoder: Decoder<SuccessfulParticipant> = succeed({
  kind: 'successful' as const,
})
  .assign('id', field('id', number))
  .assign('email', field('email', string))
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string));

const updatedParticipantDecoder: Decoder<UpdatedParticipant> = succeed({
  kind: 'updated' as const,
})
  .assign('id', field('id', number))
  .assign('email', field('email', string))
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string));

const failedParticipantDecoder: Decoder<FailedParticipant> = succeed({
  kind: 'failed' as const,
})
  .assign('email', field('email', string))
  .assign('firstName', field('first_name', string))
  .assign('lastName', field('last_name', string))
  .assign('errorMessage', field('error_message', string));

const domainMismatchAlertDecoder: Decoder<DomainMismatchAlert> = succeed({})
  .assign('email', field('email', string))
  .assign('message', field('message', string));

const summaryDecoder: Decoder<ImportResult['summary']> = succeed({})
  .assign('total', field('total', number))
  .assign('successful', field('successful', number))
  .assign('updated', field('updated', number))
  .assign('failed', field('failed', number));

const importResultDecoder: Decoder<ImportResult> = succeed({})
  .assign(
    'successfulParticipants',
    field('successful_participants', array(successfulParticipantDecoder)),
  )
  .assign('updatedParticipants', field('updated_participants', array(updatedParticipantDecoder)))
  .assign('failedParticipants', field('failed_participants', array(failedParticipantDecoder)))
  .assign(
    'participantsOfSameOrg',
    field('participants_of_same_org', array(failedParticipantDecoder)),
  )
  .assign(
    'participantsOfDifferentOrg',
    field('participants_of_different_org', array(failedParticipantDecoder)),
  )
  .assign('formattingIssue', field('formatting_issue', array(failedParticipantDecoder)))
  .assign(
    'domainMismatchAlerts',
    field('domain_mismatch_alerts', array(domainMismatchAlertDecoder)),
  )
  .assign('summary', field('summary', summaryDecoder));

export const importResultResourceDecoder: Decoder<ImportResultResource> =
  resourceDecoder(importResultDecoder);
